import type { LectureVideo } from '@/lib/types';

export type Dimension = 'koncentrace' | 'atmosfera' | 'vztahy' | 'emoce';

export const DIMENSIONS: Dimension[] = ['koncentrace', 'atmosfera', 'vztahy', 'emoce'];

export const DIMENSION_LABELS: Record<Dimension, string> = {
    koncentrace: 'Koncentrace',
    atmosfera: 'Atmosféra',
    vztahy: 'Vztahy',
    emoce: 'Emoce',
};

/** theme_key z API: `{phase}.{primary}` → lidský název týdenního tématu */
export const THEME_LABELS: Record<string, string> = {
    'zaklad.koncentrace': 'Základ · Koncentrace',
    'zaklad.atmosfera': 'Základ · Atmosféra',
    'zaklad.vztahy': 'Základ · Vztahy',
    'zaklad.emoce': 'Základ · Emoce',
    'jadro.koncentrace': 'Jádro · Koncentrace',
    'jadro.atmosfera': 'Jádro · Atmosféra',
    'jadro.vztahy': 'Jádro · Vztahy',
    'jadro.emoce': 'Jádro · Emoce',
    'upevneni.koncentrace': 'Upevnění · Koncentrace',
    'upevneni.atmosfera': 'Upevnění · Atmosféra',
    'upevneni.vztahy': 'Upevnění · Vztahy',
    'upevneni.emoce': 'Upevnění · Emoce',
};

export function themeLabel(themeKey: string | null): string {
    if (!themeKey) return 'Další téma';
    return THEME_LABELS[themeKey] ?? themeKey;
}

/**
 * Jen dimenze z `theme_key`, bez fáze — pro místa, kde se název tématu skládá
 * s dalším štítkem („Týden 4 · Koncentrace"), aby tam nebyly tři úrovně teček.
 */
export function themeDimensionLabel(themeKey: string | null): string {
    const dimension = themeDimension(themeKey);
    if (!dimension) return 'Další téma';
    return DIMENSION_LABELS[dimension];
}

/** Dimenze z klíče tématu (`faze.dimenze`), nebo null u neznámého tvaru. */
export function themeDimension(themeKey: string | null): Dimension | null {
    const dimension = themeKey?.split('.')[1] as Dimension | undefined;
    return dimension && dimension in DIMENSION_LABELS ? dimension : null;
}

export type AnswerValue =
    | 'souhlasim_naprosto'
    | 'souhlasim_spise'
    | 'nejsem_si_jisty'
    | 'nesouhlasim_spise'
    | 'nesouhlasim_naprosto';

export type AgeBand = '1_3' | '4_5';

/** Jedno měření z historie dotazníků — bez položkových odpovědí. */
export interface PlanHistoryEntry {
    id: number;
    created_at: string;
    age_band: AgeBand;
    dimension_scores: Record<Dimension, number>;
    wellbeing: number;
    primary_dimension: Dimension;
}

export type PlanAnswers = Record<string, AnswerValue>;

export interface PlanLecture {
    name: string;
    video: LectureVideo;
    methodology: string | null;
}

export interface PlanDay {
    position: number;
    exercise_id: string;
    is_anchor: boolean;
    why_key: string;
    is_completed: boolean;
    is_current: boolean;
    lecture: PlanLecture | null;
}

export interface PlanWeek {
    week: number;
    theme_key: string | null;
    locked: boolean;
    days: PlanDay[];
}

// Kotva pro odkaz z dashboardu — plán je dlouhý a dnešní cvičení bývá až
// někde uprostřed, takže na něj po příchodu odrolujeme.
export const CURRENT_DAY_ANCHOR = 'plan-dnes';

// Dimenze sdílí barvu se svou kategorií cvičení, aby si je učitel propojil:
// koncentrace = Soustředění (blue), atmosféra = Meditace (turquoise),
// vztahy = Laskavost a vztahy (rose), emoce = Emoce (orange).
// Třídy musí být celé řetězce — z `bg-${barva}-100` si je Tailwind nevyčte.
// Stejný jazyk jako karty cvičení: barvu nese světlé pozadí, text na něm
// zůstává neutrální. `card` je pro plochy, `dot` pro značky v SVG, kde by
// odstín 100 na bílé zanikl.
export const DIMENSION_STYLES: Record<
    Dimension,
    { card: string; dot: string }
> = {
    koncentrace: { card: 'bg-blue-100', dot: 'fill-blue-300' },
    atmosfera: { card: 'bg-turquoise-100', dot: 'fill-turquoise-300' },
    vztahy: { card: 'bg-rose-100', dot: 'fill-rose-300' },
    emoce: { card: 'bg-orange-100', dot: 'fill-orange-300' },
};

export interface PlanResponse {
    primary_dimension: Dimension;
    dimension_scores: Record<Dimension, number>;
    wellbeing: number;
    current_position: number;
    current_day_position: number | null;
    total_positions: number;
    completed_positions: number;
    is_complete: boolean;
    weeks: PlanWeek[];
}
